import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Check, X, ThumbsUp, ThumbsDown, Bell, MessageSquare, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Keywords({ user, selectedClient }) {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedKeywordId, setSelectedKeywordId] = useState(null);
  const [clientFeedback, setClientFeedback] = useState('');
  
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    search_volume: '',
    intent: 'Informational',
    target_page: '',
    notes: ''
  });

  // Determine firm ID to use
  const firmId = selectedClient?.firmId || user?.firm_id;
  
  // Role-based permissions
  const canEdit = user?.role && ['super_admin', 'internal_team'].includes(user.role);
  const isClient = user?.role === 'client';

  useEffect(() => {
    if (firmId) {
      fetchKeywords();
    }
  }, [firmId]);

  const fetchKeywords = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint if viewing another client's dashboard
      const endpoint = selectedClient 
        ? `${API}/admin/keywords/${firmId}`
        : `${API}/keywords`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeywords(response.data);
    } catch (err) {
      console.error('Error fetching keywords:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSampleData = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/keywords/seed-sample-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchKeywords();
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  const handleAddKeyword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint if viewing another client's dashboard
      const endpoint = selectedClient 
        ? `${API}/admin/keywords/${firmId}`
        : `${API}/keywords`;
        
      await axios.post(endpoint, {
        ...newKeyword,
        search_volume: parseInt(newKeyword.search_volume)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewKeyword({
        keyword: '',
        search_volume: '',
        intent: 'Informational',
        target_page: '',
        notes: ''
      });
      setShowAddModal(false);
      fetchKeywords();
    } catch (err) {
      console.error('Error adding keyword:', err);
      const errorMessage = err.response?.data?.detail || 'Error adding keyword. Please check all required fields.';
      alert(errorMessage);
    }
  };

  const handleUpdateStatus = async (keywordId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/keywords/${keywordId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchKeywords();
    } catch (err) {
      console.error('Error updating keyword:', err);
    }
  };

  // Client approval function
  const handleApproveKeyword = async (keywordId, approved) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/keywords/${keywordId}/approve`, 
        { approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchKeywords();
    } catch (err) {
      console.error('Error approving keyword:', err);
      alert(err.response?.data?.detail || 'Error approving keyword');
    }
  };

  // Client approve item function
  const handleApproveItem = async (keywordId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/approve-item`, 
        { item_type: 'keyword', item_id: keywordId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchKeywords();
    } catch (err) {
      console.error('Error approving keyword:', err);
      alert(err.response?.data?.detail || 'Error approving keyword');
    }
  };

  // Client request changes
  const handleRequestChanges = async () => {
    if (!selectedKeywordId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/request-changes`, 
        { item_type: 'keyword', item_id: selectedKeywordId, feedback: clientFeedback },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowFeedbackModal(false);
      setSelectedKeywordId(null);
      setClientFeedback('');
      fetchKeywords();
    } catch (err) {
      console.error('Error requesting changes:', err);
      alert(err.response?.data?.detail || 'Error requesting changes');
    }
  };

  // Notify for approval (Admin/Internal Team)
  const handleNotifyForApproval = async () => {
    const pendingKeywords = keywords.filter(kw => kw.status !== 'Approved' && kw.status !== 'Active');
    if (pendingKeywords.length === 0) {
      alert('No keywords pending approval');
      return;
    }

    setNotifying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notify-for-approval`, {
        item_type: 'keyword',
        item_ids: pendingKeywords.map(kw => kw.id),
        firm_id: firmId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Notification sent to client for approval');
      fetchKeywords();
    } catch (err) {
      console.error('Error sending notification:', err);
      alert(err.response?.data?.detail || 'Error sending notification');
    } finally {
      setNotifying(false);
    }
  };

  const filteredKeywords = keywords.filter(kw => 
    kw.keyword.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kw.target_page.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white';
      case 'Approved': return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
      case 'Pending Approval': return 'bg-gradient-to-r from-amber-400 to-orange-500 text-white';
      case 'Changes Requested': return 'bg-gradient-to-r from-purple-500 to-violet-600 text-white';
      case 'Rejected': return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
      default: return 'bg-slate-200 text-slate-600';
    }
  };

  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="keywords-page">
      {/* Header */}
      <div className="flex items-center justify-between opacity-0 animate-fadeInUp" style={{animationDelay: '0s', animationFillMode: 'forwards'}}>
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
            Keywords & Queries
          </h1>
          <p className="text-slate-500 text-lg">
            {isClient ? 'Review and approve keywords targeted for your firm' : 'Manage and track your target keywords'}
          </p>
        </div>
        {/* Only show buttons for Admin/Internal Team */}
        {canEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleNotifyForApproval}
              disabled={notifying}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all disabled:opacity-50"
              data-testid="notify-approval-button"
            >
              {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Notify for Approval
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300"
              data-testid="add-keyword-button"
            >
              <Plus className="w-5 h-5" />
              Add Keyword
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white/50 border border-slate-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            data-testid="search-keywords-input"
          />
        </div>
      </div>

      {/* Keywords Table */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow-lg opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredKeywords.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <div className="text-slate-400 mb-4 text-lg">No keywords yet</div>
            {canEdit && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="text-violet-600 hover:underline text-sm font-semibold"
                >
                  Add your first keyword
                </button>
                <span className="text-slate-400">or</span>
                <button
                  onClick={handleLoadSampleData}
                  className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:shadow-lg text-sm font-semibold transition-all"
                  data-testid="load-sample-data"
                >
                  Load Sample Data
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full modern-table">
              <thead>
                <tr>
                  <th className="px-6 py-4 text-left">Keyword</th>
                  <th className="px-6 py-4 text-left">Search Volume</th>
                  <th className="px-6 py-4 text-left">Intent</th>
                  <th className="px-6 py-4 text-left">Target Page</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredKeywords.map((kw) => (
                  <tr key={kw.id} className="hover:bg-white/50" data-testid={`keyword-row-${kw.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-slate-900">{kw.keyword}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-sm font-medium">
                        {kw.search_volume.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                      {kw.intent}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm max-w-[200px] truncate">
                      {kw.target_page}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(kw.status)}`}>
                        {kw.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Client approval actions for Pending Approval items */}
                      {kw.status === 'Pending Approval' && isClient && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveItem(kw.id)}
                            className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                            data-testid={`approve-keyword-${kw.id}`}
                          >
                            Approved
                          </button>
                          <button
                            onClick={() => {
                              setSelectedKeywordId(kw.id);
                              setShowFeedbackModal(true);
                            }}
                            className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                            data-testid={`suggest-changes-${kw.id}`}
                          >
                            Suggested Changes
                          </button>
                        </div>
                      )}
                      {/* Admin can see status and activate if approved */}
                      {kw.status === 'Pending Approval' && canEdit && (
                        <span className="text-sm text-amber-600">Awaiting client review</span>
                      )}
                      {/* Changes Requested - show feedback */}
                      {kw.status === 'Changes Requested' && (
                        <div className="text-sm">
                          <span className="text-purple-600 font-medium">Changes Requested</span>
                          {kw.client_feedback && (
                            <p className="text-slate-500 text-xs mt-1 max-w-xs truncate" title={kw.client_feedback}>
                              "{kw.client_feedback}"
                            </p>
                          )}
                        </div>
                      )}
                      {/* Activate button - only for admin/internal team */}
                      {kw.status === 'Approved' && canEdit && (
                        <button
                          onClick={() => handleUpdateStatus(kw.id, 'Active')}
                          className="text-sm text-[#4338CA] hover:underline"
                          data-testid={`activate-keyword-${kw.id}`}
                        >
                          Activate
                        </button>
                      )}
                      {/* Client sees approved status confirmation */}
                      {kw.status === 'Approved' && isClient && (
                        <span className="text-sm text-emerald-600 font-medium">Approved - Awaiting activation</span>
                      )}
                      {kw.status === 'Active' && (
                        <span className="text-sm text-emerald-600">Active</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Keyword Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="add-keyword-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>
              Add New Keyword
            </h2>
            
            <form onSubmit={handleAddKeyword} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Keyword</label>
                  <input
                    type="text"
                    value={newKeyword.keyword}
                    onChange={(e) => setNewKeyword({ ...newKeyword, keyword: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                    placeholder="e.g., personal injury lawyer nyc"
                    data-testid="modal-input-keyword"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Search Volume</label>
                  <input
                    type="number"
                    value={newKeyword.search_volume}
                    onChange={(e) => setNewKeyword({ ...newKeyword, search_volume: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                    placeholder="e.g., 1200"
                    data-testid="modal-input-volume"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Intent</label>
                <select
                  value={newKeyword.intent}
                  onChange={(e) => setNewKeyword({ ...newKeyword, intent: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                  data-testid="modal-select-intent"
                >
                  <option value="Informational">Informational</option>
                  <option value="Transactional">Transactional</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Navigational">Navigational</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Target Page</label>
                <input
                  type="text"
                  value={newKeyword.target_page}
                  onChange={(e) => setNewKeyword({ ...newKeyword, target_page: e.target.value })}
                  required
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                  placeholder="/practice-areas/personal-injury"
                  data-testid="modal-input-target-page"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={newKeyword.notes}
                  onChange={(e) => setNewKeyword({ ...newKeyword, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                  placeholder="Additional notes..."
                  data-testid="modal-input-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                  data-testid="modal-cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-[#4338CA] text-white rounded-lg font-medium hover:bg-[#3730A3] transition-colors"
                  data-testid="modal-submit-button"
                >
                  Add Keyword
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Suggest Changes Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 soft-shadow-lg animate-fadeInUp">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
                Suggest Changes
              </h3>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Please provide your feedback for this keyword:
            </p>
            
            <textarea
              value={clientFeedback}
              onChange={(e) => setClientFeedback(e.target.value)}
              rows={4}
              placeholder="e.g., Please change the keyword focus to include 'corporate litigation lawyer.'"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="feedback-input"
            />
            
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setSelectedKeywordId(null);
                  setClientFeedback('');
                }}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestChanges}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-xl font-medium hover:shadow-lg"
                data-testid="submit-feedback-btn"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}